import { useCallback, useRef, useState } from 'react';
import { useFormik, FormikErrors, FormikHelpers } from 'formik';
import axios from 'axios';
import { SPREEDLY_ENVIRONMENT_KEY } from '@ordermentum/ui-constants';
import { expiryDateModifier, isCreditCardExpired } from '../../../utilities';
import useSpreedlySession from '../../../hooks/data/useSpreedlySession';
import useSpreedlySDK, {
  SpreedlyPaymentMethod,
  SpreedlyFieldValidity,
} from '../../../hooks/useSpreedlySDK';
import {
  SecureCardPayload,
  PaymentRequestSourceType,
} from '../../../redux/payment_methods';

export type SecureCardFormValues = {
  cardName: string;
  expiryDate: string;
  cardType: string;
  defaultAll: boolean;
};

export type SecureCardFormErrors = SecureCardFormValues & {
  submit: string;
};

/**
 * Errors for Spreedly iframe fields.
 * These are separate from Formik errors since iframes aren't Formik-controlled.
 */
export type IframeFieldErrors = {
  cardNumber?: string;
  cvv?: string;
};

// Toned down version of the add-card API response
export type AddCardPaymentResult = {
  value?: { data?: { id?: string } };
} | null;

export type UseSecureCardFormProps = {
  params: { retailerId: string };
  purchaserId?: string;
  /** Called after successful add; receives the new payment method id when the API returns it */
  onSuccess?: (paymentMethodId?: string) => void;
  onClose?: () => void;
  addCardPayment: (
    cardDetails: SecureCardPayload,
    retailerId: string
  ) => Promise<AddCardPaymentResult>;
  /** Callback fired when submitting state changes - called directly at submission start/end */
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /** Source of the payment request - determines if payment request expires ('outstanding-invoices' vs 'payment-request') */
  source?: PaymentRequestSourceType;
};

export type UseSecureCardFormReturn = {
  // Form state
  values: SecureCardFormValues;
  errors: FormikErrors<SecureCardFormErrors>;
  iframeErrors: IframeFieldErrors;
  dirty: boolean;
  isSubmitting: boolean;

  // Form handlers
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  setFieldValue: (
    field: string,
    value: string | boolean,
    shouldValidate?: boolean
  ) => void;
  resetForm: () => void;
  handleCancel: () => void;
  handleExpiryDateChange: (field: string, value: string) => void;

  // Spreedly state
  isSDKReady: boolean;
  isInitialized: boolean;
  isSessionLoading: boolean;
  cardNumberElRef: React.RefObject<HTMLDivElement>;
  cvvElRef: React.RefObject<HTMLDivElement>;
};

/**
 * Splits a full name into first and last name
 * Handles edge cases like single names or multiple middle names
 */
export const splitFullName = (
  fullName: string
): { firstName: string; lastName: string } => {
  const trimmedName = fullName.trim();

  if (!trimmedName) {
    return { firstName: '', lastName: '' };
  }

  const nameParts = trimmedName.split(/\s+/);

  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  }

  return {
    firstName: nameParts[0],
    lastName: nameParts.slice(1).join(' '),
  };
};

/**
 * Parses and validates expiry date from MM/YY format
 * Returns both month and full year (YYYY)
 *
 * @throws Error if month is not between 01-12 or year is not numeric
 */
export const parseExpiryDate = (
  expiryDate: string
): { month: string; year: string } => {
  const [month, yearShort] = expiryDate.split('/');

  if (!month || !yearShort) {
    throw new Error('Invalid expiry date format. Expected MM/YY');
  }

  // Validate month is numeric and between 01-12
  const monthNum = parseInt(month, 10);
  if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    throw new Error('Invalid month. Must be between 01 and 12');
  }

  const yearNum = parseInt(yearShort, 10);
  if (Number.isNaN(yearNum)) {
    throw new Error('Invalid year format. Must be numeric');
  }

  // Convert YY to YYYY (assumes 20XX)
  const year = `20${yearShort}`;

  return { month, year };
};

/**
 * Normalizes card type from Spreedly to your system's issuer format
 */
export const normalizeCardType = (spreedlyCardType: string): string => {
  const cardTypeMap: Record<string, string> = {
    american_express: 'amex',
    visa: 'visa',
    mastercard: 'mastercard',
    discover: 'discover',
    diners_club: 'diners',
    jcb: 'jcb',
  };

  const normalized = cardTypeMap[spreedlyCardType.toLowerCase()];
  return normalized || spreedlyCardType.toLowerCase();
};

/**
 * Parses Spreedly tokenization errors into user-friendly messages
 */
export const getTokenizationErrorMessage = (error: unknown): string => {
  const defaultMessage =
    'Failed to process card information. Please try again.';

  if (!(error instanceof Error)) {
    return defaultMessage;
  }

  const errorString = error.message.toLowerCase();

  if (
    errorString.includes('card_type') ||
    errorString.includes('unable to determine the card type')
  ) {
    return 'Please enter a valid card number.';
  }

  if (errorString.includes('cvv')) {
    return 'Please enter a valid CVV.';
  }

  if (errorString.includes('expired')) {
    return 'This card has expired. Please use a different card.';
  }

  if (errorString.includes('timed out')) {
    return 'The request timed out. Please check your connection and try again.';
  }

  return defaultMessage;
};

/**
 * Extracts error message from API errors, prioritizing Axios response.data.message
 */
export const getApiErrorMessage = (
  error: unknown,
  defaultMessage = 'Failed to save card. Please try again.'
): string => {
  // Check for Axios error with response.data.message
  if (axios.isAxiosError(error) && error.response?.data?.message) {
    return error.response.data.message;
  }

  // Fallback to standard Error message
  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
};

type FormErrors = { cardName?: string; expiryDate?: string };
type ValidationResult = {
  formErrors: FormErrors;
  iframeErrors: IframeFieldErrors;
  expiryMonth: string;
  expiryYear: string;
  firstName: string;
  lastName: string;
  hasErrors: boolean;
};

/** Required-field messages (aligned with paymentFormSchema where applicable) */
const REQUIRED_FIELD_MESSAGES = {
  cardName: 'Card name is required',
  expiryDate: 'Card expiry date is required',
} as const;

/**
 * Validates form fields and iframe fields before async validation/tokenization.
 * Hierarchy: required first (field present), then validity (format/expired for non-hosted;
 * for hosted iframe fields we only set iframe errors).
 * Collects all errors so they can be displayed at once.
 */
export const validateFormAndIframeFields = (
  values: SecureCardFormValues,
  fieldValidity: SpreedlyFieldValidity
): ValidationResult => {
  const formErrors: FormErrors = {};
  const iframeErrors: IframeFieldErrors = {};
  let expiryMonth = '';
  let expiryYear = '';
  let firstName = '';
  let lastName = '';

  // Cardholder name: required first, then valid (first + last)
  const cardNameTrimmed = values.cardName?.trim() ?? '';
  if (!cardNameTrimmed) {
    formErrors.cardName = REQUIRED_FIELD_MESSAGES.cardName;
  } else {
    const split = splitFullName(values.cardName);
    firstName = split.firstName;
    lastName = split.lastName;
    if (!firstName || !lastName) {
      formErrors.cardName = 'Must include first and last name';
    }
  }

  // Expiry date: required first, then format, then not expired
  const expiryTrimmed = values.expiryDate?.trim() ?? '';
  if (!expiryTrimmed) {
    formErrors.expiryDate = REQUIRED_FIELD_MESSAGES.expiryDate;
  } else {
    try {
      const parsed = parseExpiryDate(values.expiryDate);
      expiryMonth = parsed.month;
      expiryYear = parsed.year;
      if (isCreditCardExpired(Number(expiryMonth), Number(expiryYear))) {
        formErrors.expiryDate = 'Card is expired';
      }
    } catch {
      formErrors.expiryDate = 'Invalid expiry date format';
    }
  }

  // Hosted iframe fields: we only set iframe errors
  if (!fieldValidity.validNumber) {
    iframeErrors.cardNumber = 'Please enter a valid card number.';
  }
  if (!fieldValidity.validCvv) {
    iframeErrors.cvv = 'Please enter a valid CVV.';
  }

  const hasErrors =
    Object.keys(formErrors).length > 0 || Object.keys(iframeErrors).length > 0;

  return {
    formErrors,
    iframeErrors,
    expiryMonth,
    expiryYear,
    firstName,
    lastName,
    hasErrors,
  };
};

/**
 * Applies validation errors to Formik form state.
 */
export const applyValidationErrors = (
  formErrors: FormErrors,
  iframeErrors: IframeFieldErrors,
  actions: FormikHelpers<SecureCardFormValues>,
  setIframeErrors: (errors: IframeFieldErrors) => void
): void => {
  if (Object.keys(formErrors).length > 0) {
    if (formErrors.cardName) {
      actions.setFieldError('cardName', formErrors.cardName);
    }

    if (formErrors.expiryDate) {
      actions.setFieldError('expiryDate', formErrors.expiryDate);
    }
  }

  if (Object.keys(iframeErrors).length > 0) {
    setIframeErrors(iframeErrors);
  }
};

export const useSecureCardForm = (
  props: UseSecureCardFormProps
): UseSecureCardFormReturn => {
  const {
    onSuccess = () => {},
    onClose,
    addCardPayment,
    params,
    purchaserId,
    onSubmittingChange,
    source,
  } = props;
  const { retailerId } = params;

  // Fetch Spreedly session data
  const { data: sessionData, isLoading: isSessionLoading } =
    useSpreedlySession();

  // Store payment method for submission
  const spreedlyPaymentMethodRef = useRef<SpreedlyPaymentMethod | null>(null);

  // Iframe field errors (separate from Formik since iframes aren't Formik-controlled)
  const [iframeErrors, setIframeErrors] = useState<IframeFieldErrors>({});

  // Initialize Spreedly SDK
  const {
    isSDKReady,
    isInitialized,
    fieldValidity,
    cardNumberElRef,
    cvvElRef,
    validate: validateSpreedly,
    tokenize,
  } = useSpreedlySDK({
    environmentKey: SPREEDLY_ENVIRONMENT_KEY,
    sessionData: sessionData ?? null,
    isLoading: isSessionLoading,
    onTokenizeSuccess: useCallback((paymentMethod: SpreedlyPaymentMethod) => {
      spreedlyPaymentMethodRef.current = paymentMethod;
    }, []),
  });

  const handleOnSubmit = useCallback(
    async (
      values: SecureCardFormValues,
      actions: FormikHelpers<SecureCardFormValues>
    ) => {
      try {
        // Reset initial iframe errors
        setIframeErrors({});
        onSubmittingChange?.(true);

        // Handle validations for:
        // - Cardholder name
        // - Expiry date
        // - Iframe fields (card number and CVV)
        const validation = validateFormAndIframeFields(values, fieldValidity);

        if (validation.hasErrors) {
          // If there are any form or iframe errors, return early
          applyValidationErrors(
            validation.formErrors,
            validation.iframeErrors,
            actions,
            setIframeErrors
          );
          return;
        }

        cardNumberElRef.current?.blur();
        cvvElRef.current?.blur();

        // Validate via Spreedly and wait for result
        const validationErrors = await validateSpreedly();
        if (validationErrors) {
          // Ideally we should be doing some tracking or analytics here
          // but since spreedly imposes that we don't add analytics to pages
          // where secure iframes exist, we will rely on console errors to help
          // support troubleshoot issues.
          console.error(
            '[SecureCardForm] Spreedly validation errors',
            validationErrors
          );
          // Map Spreedly validation errors to iframe field errors
          if (validationErrors.number || validationErrors.cvv) {
            setIframeErrors({
              cardNumber: validationErrors.number?.join(', '),
              cvv: validationErrors.cvv?.join(', '),
            });
            return;
          }
          // Handle any other validation errors via submit banner
          const errorMessages = Object.values(validationErrors).flat();
          if (errorMessages.length > 0) {
            actions.setFieldError('submit', errorMessages.join(', '));
            return;
          }
        }

        const { firstName, lastName, expiryMonth, expiryYear } = validation;

        try {
          await tokenize({
            first_name: firstName,
            last_name: lastName,
            month: expiryMonth,
            year: expiryYear,
          });
        } catch (tokenError) {
          // Ideally we should be doing some tracking or analytics here
          // but since spreedly imposes that we don't add analytics to pages
          // where secure iframes exist, we will rely on console errors to help
          // support troubleshoot issues.
          console.error('[SecureCardForm] Tokenization failed', tokenError);
          actions.setFieldError(
            'submit',
            getTokenizationErrorMessage(tokenError)
          );
          return;
        }

        const paymentMethod = spreedlyPaymentMethodRef.current;
        if (!paymentMethod?.token) {
          actions.setFieldError(
            'submit',
            'Payment tokenization failed. Please try again.'
          );
          return;
        }

        const payload: SecureCardPayload = {
          expiryMonth: Number(expiryMonth),
          expiryYear: Number(expiryYear),
          issuer: normalizeCardType(paymentMethod.card_type),
          name: values.cardName,
          defaultPayment: true,
          defaultAll: values.defaultAll,
          userId: retailerId,
          purchaserId,
          paymentMethodToken: paymentMethod.token,
          lastFourDigits: paymentMethod.last_four_digits,
          source,
        };

        let result: AddCardPaymentResult;
        try {
          result = await addCardPayment(payload, retailerId);
        } catch (apiError: unknown) {
          actions.setFieldError('submit', getApiErrorMessage(apiError));
          return;
        }

        actions.resetForm();

        onSuccess(result?.value?.data?.id);
      } catch (ex) {
        actions.setFieldError(
          'submit',
          'An unexpected error occurred. Please try again.'
        );
      } finally {
        actions.setSubmitting(false);
        // Notify parent that submission has ended
        onSubmittingChange?.(false);
      }
    },
    [
      addCardPayment,
      fieldValidity,
      retailerId,
      onSuccess,
      tokenize,
      validateSpreedly,
      purchaserId,
      onSubmittingChange,
      cardNumberElRef,
      cvvElRef,
      source,
    ]
  );

  const {
    dirty,
    handleSubmit,
    values,
    errors,
    setFieldValue,
    isSubmitting,
    resetForm,
  } = useFormik<SecureCardFormValues>({
    // We intentionally do not use Formik's validationSchema / validate here.
    // We need to collect and show all errors at once from both form fields and
    // Spreedly iframe (hosted) fields; that only works when validation runs in
    // our submit handler (validateFormAndIframeFields + applyValidationErrors).
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    initialValues: {
      cardName: '',
      expiryDate: '',
      cardType: '',
      defaultAll: false,
    },
    onSubmit: handleOnSubmit,
  });

  const handleCancel = useCallback(() => {
    resetForm();
    setIframeErrors({});
    if (onClose) {
      onClose();
    }
  }, [resetForm, onClose]);

  const handleExpiryDateChange = useCallback(
    (field: string, value: string) => {
      setFieldValue(field, expiryDateModifier(value));
    },
    [setFieldValue]
  );

  return {
    // Form state
    values,
    errors,
    iframeErrors,
    dirty,
    isSubmitting,

    // Form handlers
    handleSubmit,
    setFieldValue,
    resetForm,
    handleCancel,
    handleExpiryDateChange,

    // Spreedly state
    isSDKReady,
    isInitialized,
    isSessionLoading,
    cardNumberElRef,
    cvvElRef,
  };
};

export default useSecureCardForm;
